import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'
import { AllState } from '../../../../store'
import {
  MilestoneDetail,
  MilestoneInput,
  MilestoneDeltaInput,
  MilestoneDeleteInput,
} from '../../../../domain/entity/MilestoneEntity'
import { UserBasic } from '../../../../domain/value-object/UserBasicVO'
import { createDelta } from '../../../../domain/value-object/ItemDeltaInputVO'
import { formatDateTime } from '../../../../utils/date'
import { useMilestoneRepository } from '../../../../services/adaptors/milestoneRepositoryAdaptor'

export type Milestone = {
  uuid: string
  name: string
  milestoneDate: string
  description?: string
  iconName?: string
  color?: string
  achieved: boolean
  achievedAt?: string
  createdBy?: UserBasic
  createdAt?: string
  lockVersion: number
}

export type MilestoneEditState = 'unchanged' | 'added' | 'edited' | 'deleted'

export type MilestoneWithState = Milestone & {
  editState: MilestoneEditState
  originalData?: Milestone
}

export type MilestoneStatus = 'achieved' | 'overdue' | 'pending'

export type SortOption =
  | 'date_asc'
  | 'date_desc'
  | 'achieved_first'
  | 'unachieved_first'
  | 'created_asc'
  | 'created_desc'

export type RightPanelMode = 'view' | 'edit' | 'add'

export type FormState = {
  name: string
  milestoneDate: string
  description: string
  iconName: string
  color: string
  achieved: boolean
}

export const DEFAULT_ICON = 'mdi:cards-diamond'
export const DEFAULT_COLOR = '#d32f2f'

export const PRESET_ICONS = [
  'mdi:cards-diamond',
  'mdi:flag',
  'mdi:flag-checkered',
  'mdi:star',
  'mdi:rocket-launch',
  'mdi:package-variant-closed',
  'mdi:calendar-star',
]

export const PRESET_COLORS = [
  '#d32f2f',
  '#ed6c02',
  '#ffc107',
  '#2e7d32',
  '#1976d2',
  '#9c27b0',
]

const initialFormState: FormState = {
  name: '',
  milestoneDate: '',
  description: '',
  iconName: DEFAULT_ICON,
  color: DEFAULT_COLOR,
  achieved: false,
}

export const getMilestoneStatus = (milestone: Milestone): MilestoneStatus => {
  if (milestone.achieved) return 'achieved'
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const milestoneDate = new Date(milestone.milestoneDate)
  if (milestoneDate < today) return 'overdue'
  return 'pending'
}

export const getEditStateBackgroundColor = (
  editState: MilestoneEditState
): string => {
  switch (editState) {
    case 'added':
      return 'rgba(46, 125, 50, 0.08)'
    case 'edited':
      return 'rgba(237, 108, 2, 0.08)'
    case 'deleted':
      return 'rgba(211, 47, 47, 0.08)'
    default:
      return 'transparent'
  }
}

const toMilestoneInput = (m: MilestoneWithState): MilestoneInput => ({
  uuid: m.uuid,
  name: m.name,
  description: m.description,
  milestoneDate: m.milestoneDate,
  iconName: m.iconName,
  color: m.color,
  achieved: m.achieved,
})

const toMilestoneDeltaInput = (
  m: MilestoneWithState
): MilestoneDeltaInput | null => {
  const original = m.originalData
  if (!original) return null

  const delta: MilestoneDeltaInput = {
    uuid: m.uuid,
    lockVersion: m.lockVersion,
  }

  const nameDelta = createDelta(original.name, m.name)
  if (nameDelta) delta.name = nameDelta
  const descDelta = createDelta(original.description, m.description)
  if (descDelta) delta.description = descDelta
  const dateDelta = createDelta(original.milestoneDate, m.milestoneDate)
  if (dateDelta) delta.milestoneDate = dateDelta
  const iconDelta = createDelta(original.iconName, m.iconName)
  if (iconDelta) delta.iconName = iconDelta
  const colorDelta = createDelta(original.color, m.color)
  if (colorDelta) delta.color = colorDelta
  const achievedDelta = createDelta(original.achieved, m.achieved)
  if (achievedDelta) delta.achieved = achievedDelta

  return delta
}

const toMilestoneDeleteInput = (
  m: MilestoneWithState
): MilestoneDeleteInput => ({
  uuid: m.uuid,
  lockVersion: m.lockVersion,
})

const hasFieldChanges = (m: MilestoneWithState): boolean => {
  if (!m.originalData) return false
  return (
    m.name !== m.originalData.name ||
    m.milestoneDate !== m.originalData.milestoneDate ||
    m.description !== m.originalData.description ||
    m.iconName !== m.originalData.iconName ||
    m.color !== m.originalData.color ||
    m.achieved !== m.originalData.achieved
  )
}

type UseMilestoneDialogProps = {
  initialMilestones?: MilestoneDetail[]
  onClose: () => void
  onSaveComplete?: () => void
}

export const useMilestoneDialog = ({
  initialMilestones,
  onClose,
  onSaveComplete,
}: UseMilestoneDialogProps) => {
  const projectUuid = useSelector((state: AllState) => state.project.selected)
  const currentUser = useSelector((state: AllState) => state.user.user)
  const milestoneRepository = useMilestoneRepository()

  const [milestones, setMilestones] = useState<MilestoneWithState[]>([])
  const [isSaving, setIsSaving] = useState(false)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [rightPanelMode, setRightPanelMode] = useState<RightPanelMode>('add')
  const [selectedMilestoneUuid, setSelectedMilestoneUuid] = useState<
    string | null
  >(null)
  const [sortOption, setSortOption] = useState<SortOption>('date_asc')
  const [formState, setFormState] = useState<FormState>(initialFormState)

  const selectedMilestone = useMemo(
    () => milestones.find(m => m.uuid === selectedMilestoneUuid) ?? null,
    [milestones, selectedMilestoneUuid]
  )

  const hasChanges = useMemo(
    () => milestones.some(m => m.editState !== 'unchanged'),
    [milestones]
  )

  const resetForm = useCallback(() => {
    setFormState(initialFormState)
  }, [])

  const resetMilestones = useCallback(() => {
    if (initialMilestones) {
      setMilestones(
        initialMilestones.map(detail => ({
          uuid: detail.uuid,
          name: detail.name,
          milestoneDate: detail.milestoneDate,
          description: detail.description,
          iconName: detail.iconName,
          color: detail.color,
          achieved: detail.achieved,
          achievedAt: detail.achievedAt
            ? formatDateTime(detail.achievedAt)
            : undefined,
          createdBy: detail.createdBy,
          createdAt: formatDateTime(detail.createdAt),
          lockVersion: detail.lockVersion,
          editState: 'unchanged' as MilestoneEditState,
        }))
      )
    }
  }, [initialMilestones])

  useEffect(() => {
    resetMilestones()
  }, [resetMilestones])

  const handleDialogEntered = useCallback(() => {
    setRightPanelMode('add')
    setSelectedMilestoneUuid(null)
    resetForm()
    resetMilestones()
  }, [resetForm, resetMilestones])

  const handleCloseDialog = useCallback(() => {
    if (hasChanges) {
      setConfirmDialogOpen(true)
      return
    }
    onClose()
  }, [hasChanges, onClose])

  const handleConfirmClose = useCallback(() => {
    setConfirmDialogOpen(false)
    onClose()
  }, [onClose])

  const handleCancelClose = useCallback(() => {
    setConfirmDialogOpen(false)
  }, [])

  const handleSelectMilestone = useCallback(
    (uuid: string) => {
      const milestone = milestones.find(m => m.uuid === uuid)
      if (!milestone || milestone.editState === 'deleted') return
      setSelectedMilestoneUuid(uuid)
      setRightPanelMode('view')
    },
    [milestones]
  )

  const handleStartEdit = useCallback(
    (uuid: string) => {
      const milestone = milestones.find(m => m.uuid === uuid)
      if (!milestone || milestone.editState === 'deleted') return
      setSelectedMilestoneUuid(uuid)
      setRightPanelMode('edit')
      setFormState({
        name: milestone.name,
        milestoneDate: milestone.milestoneDate,
        description: milestone.description ?? '',
        iconName: milestone.iconName ?? DEFAULT_ICON,
        color: milestone.color ?? DEFAULT_COLOR,
        achieved: milestone.achieved,
      })
    },
    [milestones]
  )

  const handleStartAdd = useCallback(() => {
    setSelectedMilestoneUuid(null)
    setRightPanelMode('add')
    resetForm()
  }, [resetForm])

  const handleAddMilestone = useCallback(() => {
    if (!formState.name || !formState.milestoneDate) return

    const newMilestone: MilestoneWithState = {
      uuid: uuidv4(),
      name: formState.name,
      milestoneDate: formState.milestoneDate,
      description: formState.description || undefined,
      iconName: formState.iconName,
      color: formState.color,
      achieved: false,
      createdBy: currentUser
        ? {
            uuid: currentUser.uuid,
            name: currentUser.name,
          }
        : undefined,
      createdAt: formatDateTime(Date.now()),
      lockVersion: 0,
      editState: 'added',
    }

    setMilestones(prev => [...prev, newMilestone])
    setSelectedMilestoneUuid(newMilestone.uuid)
    setRightPanelMode('view')
  }, [formState, currentUser])

  const handleFormFieldChange = useCallback(
    (field: keyof FormState, value: string | boolean) => {
      setFormState(prev => ({ ...prev, [field]: value }))
    },
    []
  )

  const updateMilestoneField = useCallback(
    (uuid: string, updates: Partial<Milestone>) => {
      setMilestones(prev =>
        prev.map(m => {
          if (m.uuid !== uuid) return m
          const updated = {
            ...m,
            ...updates,
            originalData: m.originalData ?? { ...m },
          }
          const editState =
            m.editState === 'added'
              ? 'added'
              : hasFieldChanges(updated)
                ? 'edited'
                : 'unchanged'
          return { ...updated, editState }
        })
      )
    },
    []
  )

  const handleUpdateField = useCallback(
    (field: keyof FormState, value: string | boolean) => {
      if (!selectedMilestoneUuid) return
      updateMilestoneField(selectedMilestoneUuid, { [field]: value })
      setFormState(prev => ({ ...prev, [field]: value }))
    },
    [selectedMilestoneUuid, updateMilestoneField]
  )

  const handleMarkDelete = useCallback(
    (uuid: string) => {
      setMilestones(prev =>
        prev.map(m => {
          if (m.uuid !== uuid) return m
          if (m.editState === 'added') {
            return { ...m, editState: 'deleted' as MilestoneEditState }
          }
          return {
            ...m,
            editState: 'deleted' as MilestoneEditState,
            originalData: m.originalData ?? { ...m },
          }
        })
      )
      setSelectedMilestoneUuid(prev => (prev === uuid ? null : prev))
      setRightPanelMode('add')
      resetForm()
    },
    [resetForm]
  )

  const handleUndoDelete = useCallback((uuid: string) => {
    setMilestones(prev =>
      prev.map(m => {
        if (m.uuid !== uuid) return m
        return {
          ...m,
          editState: hasFieldChanges(m) ? 'edited' : 'unchanged',
        }
      })
    )
  }, [])

  const handleToggleAchieved = useCallback(
    (uuid: string) => {
      const milestone = milestones.find(m => m.uuid === uuid)
      if (!milestone) return
      updateMilestoneField(uuid, {
        achieved: !milestone.achieved,
        achievedAt: !milestone.achieved
          ? formatDateTime(Date.now())
          : undefined,
      })
    },
    [milestones, updateMilestoneField]
  )

  const handleSave = useCallback(async () => {
    if (!projectUuid) return

    const addedItems = milestones.filter(m => m.editState === 'added')
    const editedItems = milestones.filter(m => m.editState === 'edited')
    const deletedItems = milestones.filter(m => m.editState === 'deleted')

    if (
      addedItems.length === 0 &&
      editedItems.length === 0 &&
      deletedItems.length === 0
    ) {
      onClose()
      return
    }

    const added = addedItems.map(toMilestoneInput)
    const edited = editedItems
      .map(toMilestoneDeltaInput)
      .filter((d): d is MilestoneDeltaInput => d !== null)
    const deleted = deletedItems
      .filter(m => m.originalData)
      .map(toMilestoneDeleteInput)

    setIsSaving(true)
    try {
      await milestoneRepository.updateBatchDelta({ added, edited, deleted })
      onSaveComplete?.()
      onClose()
    } catch (error) {
      console.error('Failed to save milestones:', error)
    } finally {
      setIsSaving(false)
    }
  }, [milestones, projectUuid, onClose, onSaveComplete, milestoneRepository])

  const displayMilestones = useMemo(() => {
    const filtered = milestones.filter(
      m => !(m.editState === 'deleted' && !m.originalData)
    )

    return [...filtered].sort((a, b) => {
      switch (sortOption) {
        case 'date_asc':
          return a.milestoneDate.localeCompare(b.milestoneDate)
        case 'date_desc':
          return b.milestoneDate.localeCompare(a.milestoneDate)
        case 'achieved_first':
          if (a.achieved === b.achieved) {
            return a.milestoneDate.localeCompare(b.milestoneDate)
          }
          return a.achieved ? -1 : 1
        case 'unachieved_first':
          if (a.achieved === b.achieved) {
            return a.milestoneDate.localeCompare(b.milestoneDate)
          }
          return a.achieved ? 1 : -1
        case 'created_asc':
          return (a.createdAt ?? '').localeCompare(b.createdAt ?? '')
        case 'created_desc':
          return (b.createdAt ?? '').localeCompare(a.createdAt ?? '')
        default:
          return 0
      }
    })
  }, [milestones, sortOption])

  return {
    displayMilestones,
    selectedMilestone,
    hasChanges,
    isSaving,
    confirmDialogOpen,
    rightPanelMode,
    sortOption,
    setSortOption,
    formState,
    handleDialogEntered,
    handleCloseDialog,
    handleConfirmClose,
    handleCancelClose,
    handleSelectMilestone,
    handleStartEdit,
    handleStartAdd,
    handleAddMilestone,
    handleFormFieldChange,
    handleUpdateField,
    handleMarkDelete,
    handleUndoDelete,
    handleToggleAchieved,
    handleSave,
  }
}
